/**
 * 音乐管理器
 * 负责背景音乐的播放、暂停、停止等操作
 */
import { AudioManager } from './audio-manager';
import type { AudioResource, PlayOptions, AudioConfigMap } from './types';
import { getPublicUrl } from '@/utils/cdn';

export class MusicManager {
  private audioManager: AudioManager;
  private resources: Map<string, AudioResource> = new Map();
  private currentMusic: HTMLAudioElement | null = null;
  private currentMusicId: string | null = null;
  private fadeInterval: number | null = null;

  constructor(audioManager: AudioManager) {
    this.audioManager = audioManager;
  }

  /**
   * 注册音频资源
   */
  register(id: string, path: string): void {
    this.resources.set(id, {
      id,
      path: getPublicUrl(path),
      loaded: false,
    });
  }

  /**
   * 批量注册音频资源
   */
  registerAll(configMap: AudioConfigMap): void {
    Object.entries(configMap).forEach(([id, path]) => {
      this.register(id, path);
    });
  }

  /**
   * 检查资源是否已注册
   */
  isRegistered(id: string): boolean {
    return this.resources.has(id);
  }

  /**
   * 取消注册/卸载音频资源
   */
  unregister(id: string): void {
    const resource = this.resources.get(id);
    if (resource?.audioElement) {
      resource.audioElement.pause();
      resource.audioElement.src = '';
      resource.audioElement.load();
      resource.audioElement = undefined;
    }
    this.resources.delete(id);
  }

  /**
   * 预加载音频
   */
  async preload(id: string): Promise<void> {
    const resource = this.resources.get(id);
    if (!resource) {
      throw new Error(`Music resource "${id}" is not registered`);
    }

    if (resource.loaded && resource.audioElement) {
      return;
    }

    return new Promise((resolve, reject) => {
      const audio = new Audio(resource.path);
      audio.preload = 'auto';

      audio.addEventListener('canplaythrough', () => {
        resource.audioElement = audio;
        resource.loaded = true;
        resolve();
      });

      audio.addEventListener('error', (e) => {
        reject(new Error(`Failed to load music "${id}": ${(e.target as HTMLAudioElement).error?.message || 'Unknown error'}`));
      });

      audio.load();
    });
  }

  /**
   * 获取或创建音频元素
   */
  private getOrCreateAudioElement(id: string): HTMLAudioElement {
    const resource = this.resources.get(id);
    if (!resource) {
      throw new Error(`Music resource "${id}" is not registered`);
    }

    if (resource.audioElement) {
      return resource.audioElement;
    }

    // 创建新的音频元素
    const audio = new Audio(resource.path);
    audio.preload = 'auto';
    resource.audioElement = audio;
    return audio;
  }

  /**
   * 播放音乐（循环）
   */
  async playMusic(id: string, options: PlayOptions = {}): Promise<void> {
    // 检查全局音乐开关
    if (!this.audioManager.getMusicEnabled()) {
      return;
    }

    // 如果正在播放同一首音乐，直接返回
    if (this.currentMusicId === id && this.currentMusic && !this.currentMusic.paused) {
      return;
    }

    // 停止当前音乐
    this.stopMusic();

    try {
      const audio = this.getOrCreateAudioElement(id);
      const volume = this.audioManager.calculateMusicVolume(1.0, options.volume ?? 1.0);

      audio.volume = volume;
      audio.loop = options.loop ?? true;

      if (options.onEnded) {
        audio.addEventListener('ended', options.onEnded, { once: true });
      }

      if (options.onError) {
        audio.addEventListener('error', () => {
          options.onError?.(new Error(`Failed to play music "${id}"`));
        }, { once: true });
      }

      // 尝试播放（可能需要用户交互）
      try {
        await audio.play();
        this.currentMusic = audio;
        this.currentMusicId = id;
      } catch (error) {
        // 浏览器可能阻止自动播放
        console.warn('Music autoplay was prevented:', error);
      }
    } catch (error) {
      console.error(`Failed to play music "${id}":`, error);
      options.onError?.(error as Error);
    }
  }

  /**
   * 停止当前音乐
   */
  stopMusic(): void {
    if (this.currentMusic) {
      this.currentMusic.pause();
      this.currentMusic.currentTime = 0;
      this.currentMusic = null;
      this.currentMusicId = null;
    }
    this.clearFadeInterval();
  }

  /**
   * 暂停当前音乐
   */
  pauseMusic(): void {
    if (this.currentMusic) {
      this.currentMusic.pause();
    }
  }

  /**
   * 恢复当前音乐
   */
  resumeMusic(): void {
    if (this.currentMusic && this.audioManager.getMusicEnabled()) {
      this.currentMusic.play().catch((error) => {
        console.warn('Failed to resume music:', error);
      });
    }
  }

  /**
   * 清除淡入淡出定时器
   */
  private clearFadeInterval(): void {
    if (this.fadeInterval !== null) {
      clearInterval(this.fadeInterval);
      this.fadeInterval = null;
    }
  }

  /**
   * 淡入播放音乐
   */
  async fadeInMusic(id: string, duration: number = 1.0): Promise<void> {
    await this.playMusic(id, { loop: true });
    if (!this.currentMusic || !this.audioManager.getMusicEnabled()) {
      return;
    }

    const targetVolume = this.audioManager.calculateMusicVolume(1.0, 1.0);
    const startVolume = 0;
    const startTime = Date.now();
    const durationMs = duration * 1000;

    this.currentMusic.volume = startVolume;

    this.clearFadeInterval();
    this.fadeInterval = window.setInterval(() => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / durationMs, 1);
      const currentVolume = startVolume + (targetVolume - startVolume) * progress;

      if (this.currentMusic) {
        this.currentMusic.volume = currentVolume;
      }

      if (progress >= 1) {
        this.clearFadeInterval();
      }
    }, 16); // 约 60fps
  }

  /**
   * 淡出停止音乐
   */
  fadeOutMusic(duration: number = 1.0, callback?: () => void): void {
    if (!this.currentMusic) {
      callback?.();
      return;
    }

    const startVolume = this.currentMusic.volume;
    const startTime = Date.now();
    const durationMs = duration * 1000;

    this.clearFadeInterval();
    this.fadeInterval = window.setInterval(() => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / durationMs, 1);
      const currentVolume = startVolume * (1 - progress);

      if (this.currentMusic) {
        this.currentMusic.volume = currentVolume;
      }

      if (progress >= 1) {
        this.clearFadeInterval();
        this.stopMusic();
        callback?.();
      }
    }, 16); // 约 60fps
  }

  /**
   * 更新当前音乐音量（响应全局音量变化）
   */
  updateVolume(): void {
    if (this.currentMusic && this.currentMusicId) {
      const volume = this.audioManager.calculateMusicVolume(1.0, 1.0);
      this.currentMusic.volume = volume;

      // 如果音乐被关闭，暂停播放
      if (!this.audioManager.getMusicEnabled() && !this.currentMusic.paused) {
        this.pauseMusic();
      }
    }
  }
}

